import React, { useEffect, useState } from 'react';
import { Form, Input, Row, Col, Button, Checkbox } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import FloatingLabel from '../../../components/FloatingLabel/FloatingLabel';
import { ArrowRight } from '../../../assets/icons';
import { updateUserProfile } from '../../../services/authApi/userApi';
import useGetUserProfile from '../../../Hooks/useGetUserProfile';
import { toastNotify } from '../../../utils/toastNotify';
import { notOnlyNumberRegex, zipCodeRegex, cityRegex, addressPOBoxRegex, preventNonDigitKeyPress } from '../../../utils/Validations';
import StateSeeder from '../../../components/StateSeeder/StateSeeder';
import { preventReload } from '../../../utils/preventReload';
import { addUserData } from '../../../store/reducer/accountSlice';
import { ACCOUNT_TYPES } from '../../../constants/accountType.constants';
import { RESIDENTIAL_ADDRESS_DESCRIPTION, ENTITY_ADDRESS_DESCRIPTION } from '../../../utils/constant';
import { resolveStateId } from '../../../utils/statesCache';

// Shared between the residential and entity address blocks below, which otherwise repeat
// the same P.O. Box / city / zip rules with only the address-type label differing.
const poBoxRule = (addressType) => ({
  pattern: addressPOBoxRegex,
  message: `Address must be a valid ${addressType} address.  P.O. Box addresses are not allowed`,
});

const cityRules = [
  { required: true, message: 'Please enter valid city', pattern: new RegExp(cityRegex) },
  { min: 2, max: 35, message: 'Please provide a valid City value. City length should be between 2 and 35 characters.' },
];

const zipRules = [{ required: true, message: 'Please enter a valid zip code.', pattern: zipCodeRegex }];

// Builds the {residentialAdd, city, state, zipCode, apartmentNumber?} shape the backend
// expects for both `address` and `entityAddress`, falling back to the profile's existing
// stateId when the user hasn't touched the state dropdown on this pass.
const buildAddressPayload = (stateValue, fallbackStateId, { residentialAdd, city, zipCode, apartmentNumber }) => {
  const payload = {
    residentialAdd,
    city,
    state: stateValue ? stateValue.toString() : fallbackStateId?.toString(),
    zipCode,
  };
  if (apartmentNumber) payload.apartmentNumber = apartmentNumber;
  return payload;
};

const StepTwo = (props) => {
  const [address, setAddress] = useState();
  const [aptNumber, setAptNumber] = useState();
  const [city, setCity] = useState();
  const [state, setState] = useState();
  const [zipCode, setZipCode] = useState();
  const [entityAddress, setEntityAddress] = useState();
  const [entityAptNumber, setEntityAptNumber] = useState();
  const [entityCity, setEntityCity] = useState();
  const [entityState, setEntityState] = useState();
  const [entityZipCode, setEntityZipCode] = useState();
  const [sameAsResidential, setSameAsResidential] = useState(false);
  const [form] = Form.useForm();
  const [isEdit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const { userProfileData } = useGetUserProfile();
  const dispatch = useDispatch();
  const accountType = useSelector((reduxState) => reduxState.userData.userData?.accountType);
  const isEntity = accountType === ACCOUNT_TYPES.ENTITY;

  useEffect(() => {
    let isMounted = true;

    const hydrateAddressData = async () => {
      let residentialAddressRes;
      if (Object.keys(userProfileData).length > 0 && userProfileData.address !== null) {
        residentialAddressRes = userProfileData.address;
        form.setFieldsValue({
          address: residentialAddressRes?.residentialAdd,
          aptNumber: residentialAddressRes?.apartmentNumber || '',
          city: residentialAddressRes?.city,
          state: residentialAddressRes?.stateId,
          zipcode: residentialAddressRes?.zipCode,
        });
        setAddress(residentialAddressRes?.residentialAdd);
        setAptNumber(residentialAddressRes?.apartmentNumber || '');
        setCity(residentialAddressRes?.city);
        setState(residentialAddressRes?.stateId);
        setZipCode(residentialAddressRes?.zipCode);
        setEdit(true);
      }
      let entityAddressRes;
      let entityStateId;
      if (isEntity && userProfileData?.entityProfile?.entityAddress) {
        entityAddressRes = userProfileData.entityProfile.entityAddress;
        // entityAddress.state comes back as a human-readable state name (e.g. "California"),
        // not a stateId — resolve it the same way EditEntityAddress.js does, or the Select
        // can't match an option and falls back to displaying the raw (non-numeric) value.
        entityStateId = await resolveStateId(entityAddressRes?.state);
        if (!isMounted) return;
        form.setFieldsValue({
          entityAddress: entityAddressRes?.residentialAdd,
          entityAptNumber: entityAddressRes?.apartmentNumber || '',
          entityCity: entityAddressRes?.city,
          entityState: entityStateId,
          entityZipcode: entityAddressRes?.zipCode,
        });
        setEntityAddress(entityAddressRes?.residentialAdd);
        setEntityAptNumber(entityAddressRes?.apartmentNumber || '');
        setEntityCity(entityAddressRes?.city);
        setEntityState(entityStateId);
        setEntityZipCode(entityAddressRes?.zipCode);
      }
      // Pre-check "same as residential" when the saved entity address already matches the
      // residential one field-for-field (both compared as resolved numeric stateIds).
      if (residentialAddressRes && entityAddressRes) {
        const isSameAddress =
          (residentialAddressRes.residentialAdd || '') === (entityAddressRes.residentialAdd || '') &&
          (residentialAddressRes.apartmentNumber || '') === (entityAddressRes.apartmentNumber || '') &&
          (residentialAddressRes.city || '') === (entityAddressRes.city || '') &&
          (residentialAddressRes.zipCode || '') === (entityAddressRes.zipCode || '') &&
          (residentialAddressRes.stateId ?? undefined) === (entityStateId ?? undefined);
        if (isSameAddress) {
          form.setFieldsValue({ sameAsResidential: true });
          setSameAsResidential(true);
        }
      }
    };

    hydrateAddressData();

    return () => {
      isMounted = false;
    };
  }, [userProfileData]);

  useEffect(() => {
    preventReload(form);
  }, []);

  useEffect(() => {
    if (sameAsResidential) {
      form.setFieldsValue({
        entityAddress: address,
        entityAptNumber: aptNumber,
        entityCity: city,
        entityState: state,
        entityZipcode: zipCode,
      });
      setEntityAddress(address);
      setEntityAptNumber(aptNumber);
      setEntityCity(city);
      setEntityState(state);
      setEntityZipCode(zipCode);
    }
  }, [sameAsResidential, address, aptNumber, city, state, zipCode]);

  const handleSameAsResidentialChange = (checked) => {
    setSameAsResidential(checked);
  };

  const onStepTwoSubmit = async (values) => {
    setLoading(true);
    const { address, aptNumber, city, zipcode, entityAddress, entityAptNumber, entityCity, entityZipcode } = values;
    const stateID = Object.keys(userProfileData).length > 0 && userProfileData?.address?.stateId;
    const addressData = {
      address: buildAddressPayload(state, stateID, { residentialAdd: address, city, zipCode: zipcode, apartmentNumber: aptNumber }),
    };
    if (isEntity) {
      const entityStateID = Object.keys(userProfileData).length > 0 && userProfileData?.entityProfile?.entityAddress?.stateId;
      addressData.entityAddress = buildAddressPayload(entityState, entityStateID, {
        residentialAdd: entityAddress,
        city: entityCity,
        zipCode: entityZipcode,
        apartmentNumber: entityAptNumber,
      });
    }
    try {
      const resp = await updateUserProfile(addressData);

      if (!resp.isError) {
        !isEdit ? props.nextAction('StepThree') : props.nextAction('StepSix');
        dispatch(addUserData({ userData: resp?.data?.user }));
      } else {
        toastNotify('error', resp?.message);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Form
      name='stepTwoForm'
      form={form}
      className='auth-form steptwo'
      onFinish={onStepTwoSubmit}
      autoComplete='off'
    >
      <p className='fw-600 mb-8'>Residential Address</p>
      <p className='mb-24'>{RESIDENTIAL_ADDRESS_DESCRIPTION}</p>
      <FloatingLabel label='Residential address' inputId='address' isEdit={isEdit} inputValue={address}>
        <Form.Item
          validateTrigger="onBlur"
          name='address'
          rules={[
            {
              required: true,
              message: 'Please enter your address',
            },
            {
              message: 'Please enter valid address',
              pattern: notOnlyNumberRegex,
            },
            poBoxRule('residential'),
            {
              min: 2,
              max: 49,
              message: 'Please provide a valid residential address value. Residential address length should be between 2 and 49.',
            },
          ]}
        >
          <Input id='address' value={address} onChange={(e) => setAddress(e.target.value)} />
        </Form.Item>
      </FloatingLabel>
      <FloatingLabel label='C/O, Apt, Suite, Unit' inputId='aptNumber' isEdit={isEdit} inputValue={aptNumber}>
        <Form.Item
          validateTrigger="onBlur"
          name='aptNumber'
          rules={[
            {
              min: 1,
              max: 32,
              message: 'Please provide a valid apartment number value. Apartment number length should be between 1 and 32.',
            },
            poBoxRule('residential'),
          ]}
        >
          <Input id='aptNumber' value={aptNumber} onChange={(e) => setAptNumber(e.target.value)} />
        </Form.Item>
      </FloatingLabel>

      <Row gutter={16}>
        <Col span={24} xl={12}>
          <FloatingLabel label='City' inputId='city' isEdit={isEdit} inputValue={city}>
            <Form.Item validateTrigger="onBlur" name='city' rules={cityRules}>
              <Input maxLength={'35'} id='city' value={city} onChange={(e) => setCity(e.target.value)} />
            </Form.Item>
          </FloatingLabel>
        </Col>
        <Col span={24} md={12} xl={6}>
          <StateSeeder value={state} setState={(value) => setState(value)} isEdit={isEdit} required={true} />
        </Col>
        <Col span={24} md={12} xl={6}>
          <FloatingLabel label='Zip code' inputId='zipCode' isEdit={isEdit} inputValue={zipCode}>
            <Form.Item validateTrigger="onBlur" name='zipcode' rules={zipRules}>
              <Input
                maxLength={5}
                id='zipCode'
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                onKeyPress={preventNonDigitKeyPress}
              />
            </Form.Item>
          </FloatingLabel>
        </Col>
      </Row>
      {isEntity && (
        <>
          <Form.Item name='sameAsResidential' valuePropName='checked' className='mb-16'>
            <Checkbox checked={sameAsResidential} onChange={(e) => handleSameAsResidentialChange(e.target.checked)}>
              My entity address is the same as my residential address.
            </Checkbox>
          </Form.Item>
          <p className='fw-600 mb-8'>Entity Address</p>
          <p className='mb-24'>{ENTITY_ADDRESS_DESCRIPTION}</p>
          <FloatingLabel label='Entity address' inputId='entityAddress' inputValue={entityAddress}>
            <Form.Item
              validateTrigger='onBlur'
              name='entityAddress'
              rules={[
                { required: true, message: 'Please enter the entity address' },
                { message: 'Please enter valid address', pattern: notOnlyNumberRegex },
                poBoxRule('entity'),
                { min: 2, max: 49, message: 'Entity address length should be between 2 and 49.' },
              ]}
            >
              <Input
                id='entityAddress'
                disabled={sameAsResidential}
                value={entityAddress}
                onChange={(e) => setEntityAddress(e.target.value)}
              />
            </Form.Item>
          </FloatingLabel>
          <FloatingLabel label='C/O, Apt, Suite, Unit' inputId='entityAptNumber' inputValue={entityAptNumber}>
            <Form.Item
              validateTrigger='onBlur'
              name='entityAptNumber'
              rules={[
                { min: 1, max: 32, message: 'Apartment number length should be between 1 and 32.' },
                poBoxRule('entity'),
              ]}
            >
              <Input
                id='entityAptNumber'
                disabled={sameAsResidential}
                value={entityAptNumber}
                onChange={(e) => setEntityAptNumber(e.target.value)}
              />
            </Form.Item>
          </FloatingLabel>
          <Row gutter={16}>
            <Col span={24} xl={12}>
              <FloatingLabel label='City' inputId='entityCity' inputValue={entityCity}>
                <Form.Item validateTrigger='onBlur' name='entityCity' rules={cityRules}>
                  <Input
                    maxLength={35}
                    id='entityCity'
                    disabled={sameAsResidential}
                    value={entityCity}
                    onChange={(e) => setEntityCity(e.target.value)}
                  />
                </Form.Item>
              </FloatingLabel>
            </Col>
            <Col span={24} md={12} xl={6}>
              <StateSeeder
                value={entityState}
                setState={(value) => setEntityState(value)}
                name='entityState'
                inputId='entityState'
                isEdit={isEdit}
                required={true}
                disabled={sameAsResidential}
              />
            </Col>
            <Col span={24} md={12} xl={6}>
              <FloatingLabel label='Zip code' inputId='entityZipCode' inputValue={entityZipCode}>
                <Form.Item validateTrigger='onBlur' name='entityZipcode' rules={zipRules}>
                  <Input
                    maxLength={5}
                    id='entityZipCode'
                    disabled={sameAsResidential}
                    value={entityZipCode}
                    onChange={(e) => setEntityZipCode(e.target.value)}
                    onKeyPress={preventNonDigitKeyPress}
                  />
                </Form.Item>
              </FloatingLabel>
            </Col>
          </Row>
        </>
      )}
      <Form.Item className='auth-form__fixed-submit' shouldUpdate>
        {() => (
          <Button
            type='primary'
            htmlType='submit'
            className='ant-btn-primary--alt'
            loading={loading}
            disabled={!!form.getFieldsError().filter(({ errors }) => errors.length).length}
          >
            Continue <ArrowRight marginLeft='10px' verticalAlign='middle' top='-2px' />
          </Button>
        )}
      </Form.Item>
    </Form>
  );
};

StepTwo.propTypes = {
  nextAction: PropTypes.func,
};

export default StepTwo;